import React, { useEffect, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Download, X } from 'lucide-react';

interface VideoViewerModalProps {
  interviewId: string;
  expertName: string;
  open: boolean;
  onClose: () => void;
  recordingSize?: number;
}

export function VideoViewerModal({
  interviewId,
  expertName,
  open,
  onClose,
  recordingSize
}: VideoViewerModalProps) {
  const videoRef = useRef<HTMLVideoElement>(null);

  // Cleanup video when modal closes
  useEffect(() => {
    if (!open && videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }
  }, [open]);

  const handleDownload = () => {
    window.open(`/api/expert/interview/video/${interviewId}`, '_blank');
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden flex flex-col" hideCloseButton>
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle className="text-xl font-semibold">
              Interview with {expertName}
            </DialogTitle>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={handleDownload}
                className="bg-purple-50 hover:bg-purple-100 border-purple-200 dark:bg-purple-950 dark:hover:bg-purple-900 dark:border-purple-800 dark:text-purple-200"
              >
                <Download className="w-4 h-4 mr-1" />
                Download {recordingSize && `(${(recordingSize / (1024 * 1024)).toFixed(1)} MB)`}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={onClose}
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-hidden bg-black rounded-lg">
          <video
            ref={videoRef}
            controls
            className="w-full h-full"
            style={{ maxHeight: 'calc(90vh - 120px)' }}
            preload="metadata"
          >
            <source
              src={`/api/expert/interview/video/${interviewId}`}
              type="video/webm"
            />
            Your browser does not support the video tag.
          </video>
        </div>

        <div className="text-sm text-muted-foreground mt-2">
          <p>Tip: Right-click on the video to access additional playback options.</p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
